import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import DateRangePicker from "./DateRangePicker";
import NotificationIndicator from "./NotificationIndicator";
import logo from "./logo.png";
import { getCurrentUser, hasPermission, logout } from "../../auth/auth";

const SIDEBAR_STORAGE_KEY = "dibrownie-sidebar-collapsed";

const NavigationHeader = ({
  isCollapsed: controlledCollapsed,
  onToggleSidebar,
  notificationCount = 0,
  onDateChange,
  currentDateRange,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentUser, setCurrentUser] = useState(() => getCurrentUser());
  const [storedCollapsed, setStoredCollapsed] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
  });
  const isCollapsed = typeof controlledCollapsed === "boolean" ? controlledCollapsed : storedCollapsed;

  const navigationItems = [
    {
      label: "Visão Geral",
      permission: "overview",
      path: "/visao-geral",
      icon: "BarChart3",
      tooltip: "Visão geral com os principais KPIs do sistema",
      matcher: (pathname) => pathname === "/" || pathname === "/visao-geral" || pathname === "/financial-overview-dashboard",
    },
    {
      label: "Financeiro",
      permission: "finance",
      path: "/financeiro",
      icon: "Landmark",
      tooltip: "Central financeiro com DRE, contas a pagar, contas a receber e cobranças",
      matcher: (pathname) =>
        pathname === "/financeiro" ||
        pathname.startsWith("/financeiro/") ||
        pathname === "/accounts-receivable-management-dashboard" ||
        pathname === "/billing-automation-dashboard" ||
        pathname === "/accounts-payable-management-dashboard",
    },
    { label: "Estoque", permission: "stock", path: "/inventory-analytics-dashboard", icon: "Package", tooltip: "Análise de inventário e produtos" },
    { label: "Estoque Produto Acabado", permission: "finished_stock", path: "/estoque-produto-acabado", icon: "PackageCheck", tooltip: "Controle manual de estoque dos produtos acabados" },
    { label: "PCP", permission: "pcp", path: "/pcp", icon: "Factory", tooltip: "Planejamento e Controle de Produção com páginas internas" },
    { label: "Rotas (Mercos)", permission: "mercos_routes", path: "/mercos-rotas-dashboard", icon: "Route", tooltip: "Rotas vindas do Mercos", hiddenFromMenu: true },
    { label: "Roteiros Manuais", permission: "manual_routes", path: "/mercos-rotas-planejadas", icon: "MapPinned", tooltip: "Roteiros manuais e versões históricas", hiddenFromMenu: true },
    { label: "Promotor de Vendas", permission: "ipdv_promoter", promoterOnly: true, path: "/promotor-vendas", icon: "Store", tooltip: "Visão geral e rotas do promotor" },
    { label: "Comercial", permission: "commercial", path: "/comercial", icon: "BriefcaseBusiness", tooltip: "Módulo comercial com romaneio, rotas, combustível e promotor" },
    { label: "Budget Comercial", permission: "commercial_budget", path: "/budget-comercial", icon: "WalletCards", tooltip: "Budget, ações comerciais, retorno e ROI" },
    { label: "Controle de Degustação", permission: "tastings", path: "/controle-degustacao", icon: "CupSoda", tooltip: "Planejamento, execução e retorno das degustações por PDV" },
    { label: "Gestão de Processos", permission: "processes", path: "/processos", icon: "Workflow", tooltip: "Painel executivo, POP-FIN-04, matriz DRE, auditoria e integrações" },
    { label: "Supervisão de Vendas", permission: "super_jhonny", path: "/super-jhonny", icon: "Crown", tooltip: "Supervisão comercial com duas empresas eGestor consolidadas" },
    { label: "Tarefas", permission: "tasks", path: "/tarefas", icon: "KanbanSquare", tooltip: "Quadro Kanban para organizar e acompanhar tarefas" },
    { label: "Administração", permission: "admin", path: "/administracao", icon: "ShieldCheck", tooltip: "Usuários, senhas, permissões e auditoria" },
  ];

  // Os módulos legados continuam registrados e acessíveis com a permissão existente, mas sem atalhos no menu.
  const visibleNavigationItems = navigationItems.filter((item) => !item.hiddenFromMenu && (currentUser?.userType === "sales_promoter" ? item.promoterOnly === true : !item.promoterOnly) && hasPermission(item.permission, currentUser));

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    window.setTimeout(() => setIsRefreshing(false), 1200);
  };

  const handleToggleSidebar = () => {
    const next = !isCollapsed;
    if (typeof controlledCollapsed !== "boolean") setStoredCollapsed(next);
    window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(next));
    onToggleSidebar?.(next);
  };

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  useEffect(() => {
    document.documentElement.classList.add("has-app-sidebar");
    document.documentElement.dataset.sidebar = isCollapsed ? "collapsed" : "expanded";
    return () => {
      document.documentElement.classList.remove("has-app-sidebar");
      delete document.documentElement.dataset.sidebar;
    };
  }, [isCollapsed]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsMobileMenuOpen(false);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileMenuOpen || window.innerWidth >= 768) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const handleAuthChange = (event) => setCurrentUser(event.detail || null);
    window.addEventListener("dibrownie-auth-change", handleAuthChange);
    return () => window.removeEventListener("dibrownie-auth-change", handleAuthChange);
  }, []);

  const renderNavigation = ({ mobile = false } = {}) => (
    <nav className={mobile ? "space-y-1.5 px-3 py-4" : "sidebar-scroll min-h-0 flex-1 space-y-1.5 overflow-y-auto px-3 py-4"}>
      {visibleNavigationItems.map((item) => {
        const isActive = item.matcher ? item.matcher(location?.pathname || "") : location?.pathname === item.path;
        return (
          <button
            key={item.path}
            type="button"
            onClick={() => handleNavigation(item.path)}
            className={`group relative flex min-h-[48px] w-full items-center rounded-xl text-left transition-colors duration-150 ${mobile || !isCollapsed ? "gap-3 px-3.5" : "justify-center px-2"} ${isActive ? "bg-[#f59e0b] text-[#321a11] shadow-[0_3px_10px_rgba(245,158,11,0.28)]" : "text-[#fff8f0] hover:bg-[#563328] hover:text-white"}`}
            title={!mobile && isCollapsed ? `${item.label} — ${item.tooltip}` : item.tooltip}
            aria-current={isActive ? "page" : undefined}
          >
            <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? "bg-[#fff0cf] text-[#4b2e23]" : "bg-[#573328] text-white group-hover:bg-[#684034]"}`}>
              <Icon name={item.icon} size={19} />
            </span>
            {(mobile || !isCollapsed) && <span className="min-w-0 flex-1 text-sm font-bold leading-tight">{item.label}</span>}
            {isActive && (mobile || !isCollapsed) && <Icon name="ChevronRight" size={16} className="shrink-0" />}
            {!mobile && isCollapsed && isActive && <span className="absolute -right-3 h-7 w-1 rounded-l-full bg-[#f59e0b]" />}
          </button>
        );
      })}
    </nav>
  );

  return (
    <div className="app-navigation-shell">
      <aside className={`sidebar-crisp fixed inset-y-0 left-0 z-navigation hidden flex-col border-r border-[#654033] bg-[#3e241a] text-white shadow-[4px_0_16px_rgba(62,36,26,0.18)] transition-[width] duration-300 md:flex ${isCollapsed ? "w-[84px]" : "w-[276px]"}`}>
        <div className={`flex h-[82px] shrink-0 items-center border-b border-[#654033] ${isCollapsed ? "justify-center px-3" : "gap-3 px-5"}`}>
          <button type="button" onClick={() => handleNavigation("/visao-geral")} className="flex min-w-0 items-center gap-3 text-left" title="Ir para Visão Geral">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#573328] ring-1 ring-[#704638]">
              <img src={logo} alt="Di Brownie" className="h-9 w-9 object-contain" draggable={false} />
            </span>
            {!isCollapsed && <span className="min-w-0"><span className="block truncate text-sm font-bold text-white">Di Brownie</span><span className="mt-0.5 block truncate text-[11px] font-semibold text-[#d9c4b7]">Painel de gestão</span></span>}
          </button>
        </div>

        <button
          type="button"
          onClick={handleToggleSidebar}
          className="absolute -right-4 top-[25px] z-10 flex h-8 w-8 items-center justify-center rounded-full border border-[#d8c2aa] bg-[#fffaf4] text-[#4b2e23] shadow-md transition-transform hover:scale-105"
          title={isCollapsed ? "Expandir menu" : "Recolher menu"}
          aria-label={isCollapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
        >
          <Icon name={isCollapsed ? "PanelLeftOpen" : "PanelLeftClose"} size={16} />
        </button>

        {renderNavigation()}

        <div className="shrink-0 border-t border-[#654033] p-3">
          {!isCollapsed && onDateChange && <div className="mb-3 rounded-xl bg-[#573328] p-2"><DateRangePicker onDateChange={onDateChange} currentRange={currentDateRange} /></div>}
          <div className={`flex items-center ${isCollapsed ? "flex-col gap-1" : "gap-1"}`}>
            <button type="button" onClick={handleRefresh} disabled={isRefreshing} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[#f6e9df] transition-colors hover:bg-[#573328] hover:text-white" title="Atualizar dados">
              <Icon name="RefreshCw" size={18} className={isRefreshing ? "animate-spin" : ""} />
            </button>
            <div className="sidebar-notification"><NotificationIndicator count={notificationCount} placement="sidebar" /></div>
            {!isCollapsed && <div className="min-w-0 flex-1 px-2"><div className="truncate text-xs font-bold text-white">{currentUser?.name || "Usuário"}</div><div className="truncate text-[10px] font-medium text-[#d9c4b7]">Sessão ativa</div></div>}
            <button type="button" onClick={handleLogout} className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl text-[#f6e9df] transition-colors hover:bg-[#6b302d] hover:text-white" title="Sair do sistema">
              <Icon name="LogOut" size={18} />
            </button>
          </div>
        </div>
      </aside>

      <header className="sidebar-crisp fixed inset-x-0 top-0 z-navigation flex h-16 items-center justify-between border-b border-[#654033] bg-[#3e241a] px-3 text-white shadow-md md:hidden">
        <button type="button" onClick={() => handleNavigation("/visao-geral")} className="flex min-w-0 items-center gap-2.5">
          <img src={logo} alt="Di Brownie" className="h-9 w-9 shrink-0 object-contain" draggable={false} />
          <span className="truncate text-sm font-bold">Di Brownie Dashboard</span>
        </button>
        <div className="flex items-center gap-1">
          <NotificationIndicator count={notificationCount} />
          <button type="button" onClick={() => setIsMobileMenuOpen(true)} className="flex h-11 w-11 items-center justify-center rounded-xl hover:bg-[#573328]" aria-label="Abrir menu lateral">
            <Icon name="Menu" size={22} />
          </button>
        </div>
      </header>

      <div className={`fixed inset-0 z-[1001] bg-black/55 transition-opacity duration-200 md:hidden ${isMobileMenuOpen ? "opacity-100" : "pointer-events-none opacity-0"}`} onClick={() => setIsMobileMenuOpen(false)} aria-hidden="true" />
      <aside className={`sidebar-crisp fixed inset-y-0 left-0 z-[1002] flex w-[min(88vw,320px)] flex-col border-r border-[#654033] bg-[#3e241a] text-white shadow-xl transition-transform duration-300 md:hidden ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"}`} aria-hidden={!isMobileMenuOpen}>
        <div className="flex h-16 items-center justify-between border-b border-[#654033] px-4">
          <div className="flex items-center gap-3"><img src={logo} alt="Di Brownie" className="h-9 w-9 object-contain" /><div><div className="text-sm font-bold text-white">Di Brownie</div><div className="text-[10px] font-semibold text-[#d9c4b7]">Menu principal</div></div></div>
          <button type="button" onClick={() => setIsMobileMenuOpen(false)} className="flex h-10 w-10 items-center justify-center rounded-xl hover:bg-[#573328]" aria-label="Fechar menu"><Icon name="X" size={21} /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">{renderNavigation({ mobile: true })}</div>
        <div className="border-t border-[#654033] p-3">
          {onDateChange && <div className="mb-2"><DateRangePicker onDateChange={onDateChange} currentRange={currentDateRange} isMobile /></div>}
          <div className="flex items-center gap-2 rounded-xl bg-[#573328] p-2"><div className="min-w-0 flex-1 px-1"><div className="truncate text-xs font-bold text-white">{currentUser?.name || "Usuário"}</div><div className="text-[10px] font-medium text-[#d9c4b7]">Sessão ativa</div></div><button type="button" onClick={handleLogout} className="flex h-10 w-10 items-center justify-center rounded-lg text-[#f6e9df] hover:bg-[#6b302d] hover:text-white" title="Sair"><Icon name="LogOut" size={18} /></button></div>
        </div>
      </aside>
    </div>
  );
};

export default NavigationHeader;
